import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { PropertyDefinition } from "@/hooks/useConnectorPropertyDefinitions";

type ConnectorPropertyFieldsProps = {
  definitions: PropertyDefinition[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  disabled?: boolean;
  showAdminFields?: boolean;
};

export function ConnectorPropertyFields({
  definitions,
  values,
  onChange,
  disabled = false,
  showAdminFields = false,
}: ConnectorPropertyFieldsProps) {
  const visibleDefs = definitions.filter(
    (d) => !d.admin_only || showAdminFields,
  );

  if (visibleDefs.length === 0) return null;

  return (
    <div className="space-y-4">
      {visibleDefs.map((def) => (
        <div key={def.name} className="space-y-2">
          <Label htmlFor={`prop-${def.name}`}>
            {def.display_name}
            {def.required && <span className="ml-1 text-rose-500">*</span>}
          </Label>
          {def.field_type === "textarea" ? (
            // A MULTI-LINE value needs a multi-line control so the operator can
            // read back what they pasted. The first definition to ask for a
            // textarea — netsuite.privateKey — is a ~2.5 KB, ~40-line PEM: too
            // long to verify in a one-line box, and easily confused with the .crt.
            // NOT because a flattened PEM fails to parse — measured 2026-09-10,
            // RSA.ImportFromPem accepts a PEM whose newlines are spaces or absent;
            // only a literal backslash-n throws, and NormalisePem repairs that.
            // Before this branch existed the registry's "textarea" fell through to
            // `type="text"`, so the field_type was config the renderer ignored.
            <Textarea
              id={`prop-${def.name}`}
              placeholder={def.placeholder}
              value={values[def.name] ?? ""}
              onChange={(e) => onChange(def.name, e.target.value)}
              disabled={disabled}
              autoComplete="off"
              spellCheck={false}
            />
          ) : (
            <Input
              id={`prop-${def.name}`}
              type={def.field_type === "password" ? "password" : "text"}
              placeholder={def.placeholder}
              value={values[def.name] ?? ""}
              onChange={(e) => onChange(def.name, e.target.value)}
              disabled={disabled || def.field_type === "readonly"}
              readOnly={def.field_type === "readonly"}
              autoComplete="off"
            />
          )}
          {def.help_text && (
            <p className="text-xs text-slate-500">{def.help_text}</p>
          )}
        </div>
      ))}
    </div>
  );
}
